import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC, ReactNode } from 'react';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useGetIssueById } from 'src/hooks/queries';
import IssueForm from 'src/pages/issues/update/forms/IssueForm';
import type { IssueFormDataFields } from 'src/pages/issues/update/forms/issueSchema';
import { defaultValues } from 'src/pages/issues/update/forms/issueSchema';
import { getContributors, getOwners } from 'src/rbac/contributorHelper';
import type { ObjectWithContributors } from 'src/rbac/Permission';

import { useInsertChildIssue } from '@/hooks/mutations';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { createWithFiles } from '@/utils/createWithFiles';
import { issueDetailsUrl } from '@/utils/urls';

import type { AssessmentTypeEnum } from '../types';
import { useAssessmentTypeConfig } from '../useAssessmentTypeConfig';

type Props = {
  assessmentMode: AssessmentTypeEnum;
  readonly: boolean;
  assessmentId: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  header?: string;
};

const ConnectedIssueForm: FC<Props> = ({
  readonly,
  assessmentId,
  assessmentMode,
  id,
  onDismiss,
  header,
  beforeFieldsSlot,
}) => {
  const { data, loading: loadingIssue } = useGetIssueById({
    queryArgs: { id: id! },
    shouldSkip: !id,
  });
  const issue = data?.issue?.[0];
  const {
    routing: { resultsRegisterUrl },
  } = useAssessmentTypeConfig(assessmentMode);

  const { updateFiles } = useFileUpdate();
  const { insertChildIssue } = useInsertChildIssue();

  const onSave = async (values: IssueFormDataFields) => {
    const { files } = values;
    const { createdEntity } = await createWithFiles({
      create: () =>
        insertChildIssue({
          CustomAttributeData: values.CustomAttributeData ?? null,
          ParentId: assessmentId,
          DateIdentified: values.DateIdentified,
          DateOccurred: values.DateOccurred,
          DepartmentTypeIds:
            values.departments?.map((t) => t.DepartmentTypeId) || [],
          TagTypeIds: values.tags?.map((t) => t.TagTypeId) || [],
          Details: values.Details,
          Title: values.Title,
          Type: values.Type,
          IsExternalIssue: values.IsExternalIssue,
          ImpactsCustomer: values.ImpactsCustomer,
          ...ownerContributorAndViewerIds(values),
        }),
      getCreatedEntity: (result) => {
        const issueId = result.insertChildIssue?.Id;
        if (!issueId) {
          throw new Error('Issue id is missing');
        }

        return { id: issueId, destination: issueDetailsUrl(issueId) };
      },
      saveFiles: ({ id }) =>
        updateFiles({
          parentId: id,
          parentType: Parent_Type_Enum.Issue,
          selectedFiles: files,
          originalFiles: issue?.files.map((rf) => rf.file) ?? [],
        }),
    });

    return {
      savedIds: [createdEntity.id],
      createdEntity,
      successNavigation: { destination: resultsRegisterUrl(assessmentId) },
    };
  };

  return (
    <IssueForm
      beforeFieldsSlot={beforeFieldsSlot}
      header={header}
      onDismiss={onDismiss}
      onSave={onSave}
      values={{
        ...defaultValues,
        ...issue,
        files: issue?.files.map((f) => f.file) ?? [],
        Owners: getOwners(issue),
        Contributors: getContributors(issue),
        ancestorContributors: issue?.ancestorContributors ?? [],
      }}
      issueType={Parent_Type_Enum.Issue}
      readOnly={readonly || loadingIssue}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default ConnectedIssueForm;
